import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { FormField } from '~/eds/src/components/FormField/FormField';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { Select } from '~/eds/src/components/Select/Select';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { PilotId } from '~/types/PilotId';

import CustomFieldModal from '../../components/Modals/CustomFieldModal/CustomFieldModal';
import EcModal from '../../components/Shared/EcModal/EcModal';
import { MODAL_CUSTOM_FIELD } from '../../types/modal.types';

export const FieldModal = ({ clientId }: { clientId: string }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { toast } = useToast();

  const action = useSelector(slices.dataFields.selectors.selectAction);
  const sections = useSelector(slices.dataFields.selectors.selectSections);
  const section = useSelector(slices.dataFields.selectors.selectedSection);
  const dataField = useSelector(slices.dataFields.selectors.selectedField);

  const enableModalConsolidation = useFlag(FlagType.ModalConsolidation);

  const [deleteValue, setDeleteValue] = useState('');
  const [selectedSection, setSelectedSection] = useState<{
    value: PilotId;
    label: string;
  } | null>(null);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);

  const customFieldModalRef = useRef<{
    getFormData: () => any;
    handleActionClick: () => void;
  } | null>(null);

  useEffect(() => {
    if (action === 'move-field' && dataField && section) {
      setSelectedSection({
        value: section.id,
        label: section.label ?? '',
      });
    }
  }, [action, section, dataField]);

  const [deleteDataField, { isLoading: isDeletingField }] =
    api.endpoints.deleteDataField.useMutation();

  const [moveFieldToSection, { isLoading: isMovingField }] =
    api.endpoints.moveFieldToSection.useMutation();

  const [createDataField] = api.endpoints.createDataField.useMutation();
  const [updateDataField] = api.endpoints.updateDataField.useMutation();

  const onFieldHide = () => {
    dispatch(slices.dataFields.actions.resetConfiguration());
    setDeleteValue('');
    setSelectedSection(null);
    setIsSaveDisabled(true);
  };

  const onHide = () => {
    dispatch(slices.dataFields.actions.resetAction());
  };

  const onConfirm = (data?: any) => {
    if (action === 'delete-field') {
      if (!dataField?.id) {
        return null;
      }

      deleteDataField({ fieldId: dataField?.id })
        ?.unwrap()
        .then(() => {
          toast({
            message: intl.formatMessage({
              id: 'DataFields.fieldDeletedSuccess',
              defaultMessage: 'Field has been deleted.',
              description: 'Success message when field is deleted',
            }),
            status: 'success',
          });
          dispatch(slices.dataFields.actions.resetAction());
        })
        .catch((err: any) => {
          toast({
            message: err.message,
            status: 'danger',
          });
        });
    }
    if (action === 'add-field') {
      if (!data || !section?.id) {
        return null;
      }

      createDataField({
        data,
        sectionId: section.id,
      })
        ?.unwrap()
        .then(() => {
          toast({
            message: intl.formatMessage({
              id: 'DataFields.fieldCreatedSuccess',
              defaultMessage: 'Field has been created.',
              description: 'Success message when field is created',
            }),
            status: 'success',
          });
          onHide();
        })
        .catch((error: any) => {
          toast({
            message:
              error?.response?.data?.detail ??
              intl.formatMessage({
                id: 'DataFields.fieldCreateError',
                defaultMessage:
                  'An error occurred while creating the new field.',
                description: 'Error message when field creation fails',
              }),
            status: 'danger',
          });
        });
    }
    if (action === 'edit-field') {
      if (!data || !section?.id || !dataField?.id) {
        return null;
      }

      updateDataField({
        data,
        fieldId: dataField.id,
        sectionId: section.id,
      })
        ?.unwrap()
        .then(() => {
          toast({
            message: intl.formatMessage({
              id: 'DataFields.fieldUpdatedSuccess',
              defaultMessage: 'The field has been updated.',
              description: 'Success message when field is updated',
            }),
            status: 'success',
          });
          onFieldHide();
        })
        .catch((error: any) => {
          toast({
            message:
              error?.response?.data?.detail ??
              intl.formatMessage({
                id: 'DataFields.fieldUpdateError',
                defaultMessage: 'An error occurred while updating the field.',
                description: 'Error message when field update fails',
              }),
            status: 'danger',
          });
        });
    }
    if (action === 'move-field') {
      if (!selectedSection?.value || !dataField?.id) {
        return null;
      }

      moveFieldToSection({
        sectionId: selectedSection?.value,
        fieldId: dataField.id,
      })
        ?.unwrap()
        .then(() => {
          toast({
            message: intl.formatMessage({
              id: 'DataFields.fieldMovedSuccess',
              defaultMessage: 'Field has been moved.',
            }),
            status: 'success',
          });
          onFieldHide();
        })
        .catch((error: any) => {
          toast({
            message: error.message,
            status: 'danger',
          });
        });
    }
  };

  if (!action) return null;

  if (action === 'delete-field') {
    return (
      <Modal
        children={
          <Layout preset="form-fields">
            <Text>
              {intl.formatMessage(
                {
                  id: 'DataFields.deleteFieldConfirmation',
                  defaultMessage:
                    'Are you sure you want to delete {dataField}? You will not be able to undo this action.',
                  description:
                    'Delete field confirmation message. Example: dataField = "Custom Field 1"',
                },
                {
                  dataField: dataField.label,
                },
              )}
            </Text>
            <FormField<string, false>
              input={TextInput}
              label={intl.formatMessage({
                id: 'DataFields.deleteConfirmationLabel',
                defaultMessage: "Type in the word 'DELETE' to confirm",
                description: 'Delete confirmation form field label',
              })}
              name="Section name"
              value={deleteValue}
              onChange={(value) => setDeleteValue(value ?? '')}
            />
          </Layout>
        }
        primaryAction={{
          text: intl.formatMessage({
            id: 'DataFields.deleteButton',
            defaultMessage: 'Delete',
            description: 'Delete button text',
          }),
          level: 'danger',
          isLoading: isDeletingField,
          disabled: deleteValue !== 'DELETE',
          onClick: onConfirm,
        }}
        onCancel={onFieldHide}
        onHide={onFieldHide}
        isVisible={true}
        title={intl.formatMessage({
          id: 'DataFields.deleteFieldTitle',
          defaultMessage: 'Delete Field?',
          description: 'Delete field modal title',
        })}
      />
    );
  }
  if (action === 'add-field' || action === 'edit-field') {
    const isEditing = !!dataField;
    const titleText =
      enableModalConsolidation && !isEditing
        ? intl.formatMessage({
            id: 'DataFields.addDataFieldTitle',
            defaultMessage: 'Add Data Field',
            description: 'New data field modal title',
          })
        : !enableModalConsolidation && !isEditing
          ? intl.formatMessage({
              id: 'DataFields.newDataFieldTitle',
              defaultMessage: 'New Data Field',
              description: 'New data field modal title',
            })
          : intl.formatMessage({
              id: 'DataFields.editDataFieldTitle',
              defaultMessage: 'Edit Data Field',
              description: 'Edit data field modal title',
            });

    const confirmButtonText = isEditing
      ? intl.formatMessage({
          id: 'DataFields.saveButton',
          defaultMessage: 'Save',
          description: 'Save button text',
        })
      : intl.formatMessage({
          id: 'DataFields.addDataFieldButton',
          defaultMessage: 'Add Data Field',
          description: 'Add data field button text',
        });

    return (
      <>
        {enableModalConsolidation ? (
          <Panel
            enableBackdrop
            title={titleText}
            mode="floating"
            width="560px"
            footer={{
              actions: [
                {
                  mode: 'chip',
                  text: intl.formatMessage({
                    id: 'DataFields.cancelButton',
                    defaultMessage: 'Cancel',
                    description: 'Cancel button text',
                  }),
                  onClick: onFieldHide,
                },
                {
                  mode: 'chip',
                  text: confirmButtonText,
                  onClick: () => {
                    if (customFieldModalRef.current) {
                      const formData =
                        customFieldModalRef.current.getFormData();
                      if (formData) {
                        onConfirm(formData);
                      }
                    }
                  },
                  level: 'primary',
                  disabled: isSaveDisabled,
                  tooltip: isSaveDisabled
                    ? intl.formatMessage({
                        id: 'DataFields.fieldNameRequired',
                        defaultMessage: 'Field name is required',
                        description: 'Field name required message',
                      })
                    : undefined,
                },
              ],
            }}
            hidden={{ onHide: onFieldHide }}
            children={
              <CustomFieldModal
                modalType={MODAL_CUSTOM_FIELD}
                width="560px"
                clientId={clientId}
                selectedSection={section?.label ?? null}
                selectedItem={dataField}
                handleDataFieldChange={onConfirm}
                hideFooter={true}
                onValidationChange={setIsSaveDisabled}
                onGetFormDataRef={(ref: any) => {
                  customFieldModalRef.current = ref;
                }}
              />
            }
          />
        ) : (
          <EcModal
            modalType={MODAL_CUSTOM_FIELD}
            hideModal={onFieldHide}
            width="560px"
            title={titleText}
            clientId={clientId}
            selectedSection={section?.label ?? null}
            selectedItem={dataField}
            confirmButtonText={confirmButtonText}
            handleDataFieldChange={onConfirm}
          />
        )}
      </>
    );
  }

  if (action === 'move-field') {
    const sectionsOptions = sections.map((section) => {
      return {
        value: {
          value: section.id,
          label: section.label,
        },
        label: section.label,
      };
    });

    return (
      <Modal
        title={intl.formatMessage({
          id: 'DataFields.moveFieldToSectionTitle',
          defaultMessage: 'Move Field to Section',
          description: 'Move field to section modal title',
        })}
        children={
          <FormField
            name="Section name"
            label={intl.formatMessage(
              {
                id: 'DataFields.moveFieldLabel',
                defaultMessage: 'Where do you want to move {fieldName}?',
                description:
                  'Move field form label. Example: fieldName = "Custom Field 1"',
              },
              {
                fieldName: dataField?.label,
              },
            )}
            input={Select}
            inputProps={{
              isMulti: false,
              isClearable: false,
              options: sectionsOptions,
            }}
            value={selectedSection?.label ?? ''}
            onChange={(value) => {
              const { value: id, label } = value as unknown as {
                value: PilotId;
                label: string;
              };
              setSelectedSection({
                value: id,
                label,
              });
            }}
          />
        }
        isVisible={true}
        primaryAction={{
          text: intl.formatMessage({
            id: 'DataFields.moveButton',
            defaultMessage: 'Move',
            description: 'Move button text',
          }),
          isLoading: isMovingField,
          onClick: onConfirm,
        }}
        onCancel={onFieldHide}
        onHide={onFieldHide}
      />
    );
  }
};
